import React, { useState, useEffect, useRef, useCallback } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { ReactComponent as DownArrow } from "../../assets/svg/down_arror.svg";
import "../vendor/vendorList.css";
import { X, Check } from "lucide-react";
import { apiCallGeneric } from "../../services/api-service.js";
import ReactDOM from "react-dom";
import { Spinner } from "react-bootstrap";

const SearchableDropdown = ({
  apiUrl,
  placeholder,
  value,
  onSelect,
  handleClear,
  isOpen: controlledIsOpen,
  onToggle,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  const setIsOpen = (val) => {
    if (onToggle) {
      onToggle(val);
    } else {
      setInternalIsOpen(val);
    }
  };

  const [options, setOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const searchDebounceTimer = useRef(null);

  const fetchData = useCallback(
    async (term = "") => {
      if (!apiUrl) {
        setOptions([]);
        return;
      }

      setLoading(true);

      try {
        const response = await apiCallGeneric("get", apiUrl, null, {
          params: { term },
        });

        if (response.status === 200 && response.data.success) {
          setOptions(response.data.suggestionlist || []);
        } else {
          setOptions([]);
        }
      } catch (err) {
        console.error("Error fetching options:", err);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl]
  );

  // Lazy-load options only when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchData(search);
    }
  }, [isOpen, fetchData, search]);

  // Handle search with debouncing
  const handleSearch = (e) => {
    const term = e.target.value;
    setSearch(term);

    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    searchDebounceTimer.current = setTimeout(() => {
      fetchData(term);
    }, 300);
  };

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    placement: "bottom",
  });
  const containerRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const menuMaxHeight = 350; // approx max height of the menu

      const spaceBelow = windowHeight - rect.bottom;
      const spaceAbove = rect.top;

      if (spaceBelow < menuMaxHeight && spaceAbove > spaceBelow) {
        setMenuPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          placement: "top",
        });
      } else {
        setMenuPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
          placement: "bottom",
        });
      }
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      window.addEventListener("scroll", updatePosition);
    }
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [isOpen, updatePosition]);

  const menu = (
    <Dropdown.Menu
      renderOnMount
      show={isOpen}
      style={{
        position: "fixed",
        top:
          menuPosition.placement === "bottom"
            ? `${menuPosition.top + 5}px`
            : "auto",
        bottom:
          menuPosition.placement === "top"
            ? `${window.innerHeight - menuPosition.top + 5}px`
            : "auto",
        left: `${menuPosition.left}px`,
        width: `${menuPosition.width}px`,
        backgroundColor: "white",
        borderRadius: "12px",
        border: "none",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        minWidth: "250px",
        overflowY: "auto",
        maxHeight: "320px",
        padding: "8px",
        animation: "slideDown 0.2s ease-out",
        zIndex: 10001,
        margin: 0,
      }}
    >
      <div style={{ padding: "0 0 8px 0" }}>
        <input
          type="text"
          className="fs-14 fw-500 textcolor"
          value={search}
          onChange={handleSearch}
          placeholder={`Search ${placeholder}`}
          autoFocus
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: "8px",
            border: "1px solid #e0e0e0",
            fontSize: "14px",
            transition: "all 0.2s ease-in-out",
            outline: "none",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#6c5ce7";
            e.target.style.boxShadow = "0 0 0 0.2rem rgba(108, 92, 231, 0.15)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "#e0e0e0";
            e.target.style.boxShadow = "none";
          }}
        />
      </div>

      {loading ? (
        <div
          className="d-flex align-items-center justify-content-center py-4"
          style={{ color: "#888888" }}
        >
          <Spinner
            animation="border"
            size="sm"
            style={{ marginRight: "8px" }}
          />
          <span className="fs-14">Loading...</span>
        </div>
      ) : options.length === 0 ? (
        <div
          className="text-center py-4"
          style={{ color: "#888888", fontSize: "14px" }}
        >
          No results found
        </div>
      ) : (
        <div style={{ maxHeight: "240px", overflowY: "auto" }}>
          {options.map((option) => (
            <Dropdown.Item
              className="d-flex justify-content-between align-items-center fs-14 fw-500 textcolor"
              key={option.value}
              onClick={() => {
                onSelect(option);
                setIsOpen(false);
                setSearch("");
              }}
              active={value?.value === option.value}
              style={{
                borderRadius: "6px",
                padding: "10px 12px",
                margin: "2px 0",
                transition: "all 0.15s ease-in-out",
                backgroundColor:
                  value?.value === option.value ? "#f0ebff" : "transparent",
              }}
              onMouseEnter={(e) => {
                if (value?.value !== option.value) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  value?.value === option.value ? "#f0ebff" : "transparent";
              }}
            >
              <span>{option.label}</span>
              {value?.value === option.value && (
                <Check size={16} color="#6c5ce7" strokeWidth={2.5} />
              )}
            </Dropdown.Item>
          ))}
        </div>
      )}
    </Dropdown.Menu>
  );
  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Dropdown show={isOpen} onToggle={(nextShow) => setIsOpen(nextShow)}>
        <Dropdown.Toggle
          variant="light"
          id="dropdown-searchable"
          className="d-flex align-items-center justify-content-between"
          style={{
            borderRadius: "12px",
            border: isOpen ? "1px solid #6c5ce7" : "1px solid #EBEBEB",
            padding: "6px 12px",
            backgroundColor: "white",
            transition: "all 0.2s ease-in-out",
            outline: "none",
            boxShadow: isOpen ? "0 0 0 2px rgba(108, 92, 231, 0.1)" : "none",
            width: "100%",
          }}
          onMouseEnter={(e) => {
            if (!isOpen) {
              e.currentTarget.style.borderColor = "#d0d0d0";
            }
          }}
          onMouseLeave={(e) => {
            if (!isOpen) {
              e.currentTarget.style.borderColor = "#EBEBEB";
            }
          }}
        >
          <span className="fs-12 fw-400 textGrey">{placeholder} :</span>
          <span className="d-flex align-items-center px-1 gap-2">
            <span
              className="dropdown-text"
              style={{
                color: value?.label ? "#212129" : "#888888",
                fontWeight: value?.label ? "500" : "400",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100px",
                display: "inline-block",
                verticalAlign: "middle"
              }}
              title={value?.label || "Select"}
            >
              {value?.label || "Select"}
            </span>

            {value?.value && value?.value !== "" && value?.label !== "All" && (
              <X
                size={16}
                className="cursor-pointer"
                style={{ color: "#888888" }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                  setSearch("");
                }}
              />
            )}

            <span
              className="dropdown-toggle-arrow textPurple"
              style={{
                transition: "transform 0.2s ease-in-out",
                transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <DownArrow />
            </span>
          </span>
        </Dropdown.Toggle>
        {isOpen && ReactDOM.createPortal(menu, document.body)}
      </Dropdown>
    </div>
  );
};

export default SearchableDropdown;
